import {asyncScheduler, from, merge, Observable, of, timer} from "rxjs";
import {catchError, distinctUntilChanged, filter, last, map, mergeMap, retry, scan, share, throttleTime, timeout} from "rxjs/operators";

export interface ISettleAllOptions {
    concurrency?: number;   // max simultaneous in flight requests (default 8)
    timeoutMs?: number;     // hard per source timeout, hung request is abandoned (default 22000)
    retries?: number;       // light per source retries on error or timeout (default 1)
    retryDelayMs?: number;  // base delay between retries, grows exponentially (default 1500)
    coalesceMs?: number;    // if > 0, coalesce progressive emissions to at most one per this many ms
                            // (plus an immediate first and a guaranteed final). Stops a huge list from
                            // re-reducing and re-rendering dozens of times during load. (default 0 = every settle)
    label?: string;         // optional tag used only for diagnostic console logging
}

/**
 * Progress snapshot emitted by settleAllProgressive after every source settles.
 * `results` is CUMULATIVE - it grows as more sources succeed, so each emission is a
 * superset of the previous one. `done` is true only on the very last emission.
 */
export interface ISettleProgress<T> {
    results: T[];   // cumulative successful results so far
    settled: number; // sources finished so far (succeeded + failed)
    total: number;   // total sources
    ok: number;      // succeeded so far
    failed: number;  // errored or timed out so far
    done: boolean;   // true on the final emission (settled === total)
}

const DEFAULTS: Required<Omit<ISettleAllOptions, "label">> = {
    concurrency: 8,
    timeoutMs: 22000,
    retries: 1,
    retryDelayMs: 1500,
    coalesceMs: 0,
};

interface ISettleOutcome<T> {
    ok: boolean;
    value: T;
}

/**
 * Core resilient fan out. Runs every source with a concurrency cap, a hard per source
 * timeout, and a light retry. A source that errors or times out is DROPPED (it does not
 * abort the others). Emits an ISettleProgress snapshot after EACH source settles, with a
 * cumulative `results` array, so the UI can paint partial results the instant the first
 * chunk returns instead of waiting for the slowest one.
 *
 * Contrast with forkJoin, which emits ONLY once every source completes and aborts the
 * whole stream if ANY source errors - the cause of the enterprise dashboard hanging at
 * "0 of 0" forever when a single station chunk timed out.
 */
export function settleAllProgressive<T>(sources: Array<Observable<T>>, options: ISettleAllOptions = {}): Observable<ISettleProgress<T>> {
    const opts = {...DEFAULTS, ...options};
    const total = sources ? sources.length : 0;
    if (total === 0) {
        return of({results: [], settled: 0, total: 0, ok: 0, failed: 0, done: true});
    }

    const progress$ = from(sources).pipe(
        mergeMap((src) => src.pipe(
            timeout(opts.timeoutMs),
            retry({
                count: opts.retries,
                delay: (_err, retryIndex) => timer(Math.pow(2, retryIndex - 1) * opts.retryDelayMs),
            }),
            map((value): ISettleOutcome<T> => ({ok: true, value})),
            catchError((): Observable<ISettleOutcome<T>> => {
                return of({ok: false, value: undefined as unknown as T});
            }),
        ), opts.concurrency),
        scan((acc: ISettleProgress<T>, outcome: ISettleOutcome<T>): ISettleProgress<T> => {
            const settled = acc.settled + 1;
            return {
                results: outcome.ok ? [...acc.results, outcome.value] : acc.results,
                settled,
                total,
                ok: acc.ok + (outcome.ok ? 1 : 0),
                failed: acc.failed + (outcome.ok ? 0 : 1),
                done: settled === total,
            };
        }, {results: [], settled: 0, total, ok: 0, failed: 0, done: false}),
    );

    if (opts.coalesceMs <= 0) {
        return progress$;
    }

    /** Coalesce the firehose of per-settle snapshots into at most one emission per coalesceMs.
     *  throttleTime(leading,trailing) gives an immediate first paint plus periodic updates; the
     *  merged done filter guarantees the final complete snapshot is never swallowed by the window.
     *  distinctUntilChanged on settled drops the duplicate when both branches yield the final one. **/
    const shared$ = progress$.pipe(share());
    return merge(
        shared$.pipe(throttleTime(opts.coalesceMs, asyncScheduler, {leading: true, trailing: true})),
        shared$.pipe(filter((p) => p.done)),
    ).pipe(
        distinctUntilChanged((a, b) => a.settled === b.settled && a.done === b.done),
    );
}

/**
 * Final only convenience wrapper around settleAllProgressive: emits ONCE with the array
 * of successful results after every source has settled, then completes. Use when the
 * caller does not care about progressive painting (e.g. enrichment calls). Like
 * forkJoin but resilient - never hangs on and never aborts because of a failed source.
 */
export function settleAll<T>(sources: Array<Observable<T>>, options: ISettleAllOptions = {}): Observable<T[]> {
    return settleAllProgressive<T>(sources, options).pipe(
        last(),
        map((progress) => progress.results),
    );
}
